import {
  AbiCoder,
  BrowserProvider,
  TypedDataEncoder,
  getAddress,
  getBytes,
  hashMessage,
  keccak256,
  toUtf8Bytes,
  zeroPadValue,
  type Eip1193Provider,
} from 'ethers'
import type { Hex } from '../types.js'

/**
 * Off-chain EIP-1271 message signing for the Main Safe.
 *
 * The Main Safe is a contract — no private key. Its sole owner is the
 * Module Safe (also a contract, 2/2 Ainura + Guardian). To produce a
 * signature the Main Safe's verifier (Safe.isValidSignature) accepts:
 *
 *   1. Compute innerHash for the dApp request (eth_sign hash for
 *      personal_sign, EIP-712 hash for typed-data).
 *   2. Ainura + Guardian both sign the SafeMessage(innerHash) typed-hash
 *      with the *Module Safe's* domain. Two ECDSA sigs.
 *   3. Concat sorted by signer address ascending → innerSig (130 bytes).
 *   4. Wrap as a Safe contract signature pointing at the Module Safe:
 *      [r=padded(moduleSafeAddr) | s=offset(65) | v=0]
 *      [length(innerSig) | innerSig]
 *      Total 227 bytes.
 *   5. Return wrapper to the dApp. When it later calls
 *      Main.isValidSignature(innerHash, wrapper):
 *        - Main.checkSignatures sees v=0, calls
 *          Module.isValidSignature(data, innerSig)
 *        - Module.checkSignatures verifies the 2 ECDSA sigs against its
 *          owners → MAGIC_VALUE
 *        - Main returns MAGIC_VALUE up to the dApp.
 *
 * Pure off-chain. No gas. No queue.
 */

/**
 * EIP-712 typed-data payload as accepted by `eth_signTypedData_v4`. Looser
 * than ethers' TypedDataDomain because dApps include extra type defs.
 */
export interface EthSignTypedDataPayload {
  domain: {
    name?: string
    version?: string
    chainId?: number | string
    verifyingContract?: string
    salt?: string
  }
  types: Record<string, Array<{ name: string; type: string }>>
  primaryType: string
  message: Record<string, unknown>
}

/**
 * Compute the "inner" hash that gets embedded inside the SafeMessage(bytes
 * message) typed-data. For personal_sign that's the EIP-191 prefixed hash;
 * for eth_signTypedData_v4 that's the EIP-712 hash of the supplied payload.
 *
 * Either way, the result is the 32 bytes the dApp's verifier will pass as
 * `_dataHash` when it eventually calls the Safe's isValidSignature variants.
 */
export function computeInnerPersonalSignHash(message: string): Hex {
  // dApps send personal_sign messages either as a plain UTF-8 string or as
  // a 0x-hex blob. MetaMask / Rabby normalize by decoding valid 0x-hex
  // payloads to bytes first; ethers.hashMessage on a string would utf8-
  // encode the literal "0x..." chars instead. Match the wallet behavior.
  const isHexBlob =
    /^0x[0-9a-fA-F]*$/.test(message) && message.length % 2 === 0
  return hashMessage(isHexBlob ? getBytes(message) : message) as Hex
}

export function computeInnerTypedDataHash(
  payload: EthSignTypedDataPayload,
): Hex {
  // TypedDataEncoder requires a copy of `types` without EIP712Domain, since
  // it derives the domain separator from `domain` itself. dApps usually
  // include EIP712Domain in their types map; strip it here.
  const types = { ...payload.types }
  delete types.EIP712Domain
  return TypedDataEncoder.hash(
    payload.domain as Parameters<typeof TypedDataEncoder.hash>[0],
    types,
    payload.message,
  ) as Hex
}

/**
 * Compute the on-chain SafeMessage hash — the storage key that
 * `safe.signedMessages` is indexed by, and the value the EIP-712 fallback
 * compares against in isValidSignature. Must match exactly what
 * SignMessageLib.getMessageHash(<innerHash>) computes on-chain.
 */
export function computeSafeMessageHash(
  chainId: number,
  safeAddress: string,
  innerHash: Hex,
): Hex {
  return TypedDataEncoder.hash(
    { chainId, verifyingContract: getAddress(safeAddress) },
    { SafeMessage: [{ name: 'message', type: 'bytes' }] },
    { message: innerHash },
  ) as Hex
}

/**
 * Ask the connected EOA wallet (Ainura) to produce an EIP-712
 * SafeMessage signature against the given Safe's domain. Returns a
 * 65-byte ECDSA signature.
 */
export async function signSafeMessageAsOwner(
  walletProvider: Eip1193Provider,
  signerAddress: string,
  chainId: number,
  safeAddress: string,
  innerHash: Hex,
): Promise<Hex> {
  const browser = new BrowserProvider(walletProvider, chainId)
  const signer = await browser.getSigner(getAddress(signerAddress))
  const sig = await signer.signTypedData(
    { chainId, verifyingContract: getAddress(safeAddress) },
    { SafeMessage: [{ name: 'message', type: 'bytes' }] },
    { message: innerHash },
  )
  return sig as Hex
}

/**
 * Concatenate two ECDSA signatures (Ainura + Guardian) into Module Safe's
 * inner contract signature. Owner order matters: Safe's checkSignatures
 * requires strictly ascending addresses.
 */
export function buildInnerSignature(
  sigs: Array<{ owner: string; signature: Hex }>,
): Hex {
  const sorted = [...sigs].sort((a, b) =>
    a.owner.toLowerCase() < b.owner.toLowerCase() ? -1 : 1,
  )
  return ('0x' +
    sorted.map((s) => s.signature.replace(/^0x/, '')).join('')) as Hex
}

/**
 * Wrap an inner signature (Module Safe's 2/2 ECDSA pack) as a Safe
 * contract-signature pointing at the Module Safe — the format Main Safe's
 * checkSignatures expects when the only owner is a contract.
 *
 * Layout (65-byte static + length-prefixed dynamic):
 *   bytes 0..32:           padded contract owner address (Module Safe)
 *   bytes 32..64:          offset to dynamic data, in bytes (= 65)
 *   byte 64:               0 (signature type = contract)
 *   bytes 65..97:          length of innerSig as uint256
 *   bytes 97..(97+len):    innerSig
 *
 * For 2/2 Module Safe (innerSig = 130 bytes), the total is 227 bytes.
 */
export function encodeNestedContractSignature(
  moduleSafeAddr: string,
  innerSig: Hex,
): Hex {
  const innerLen = (innerSig.length - 2) / 2 // bytes count
  const r = zeroPadValue(getAddress(moduleSafeAddr), 32)
  const s = zeroPadValue('0x41', 32) // 0x41 = 65, the offset to dynamic
  const v = '00'
  const lengthHex = AbiCoder.defaultAbiCoder().encode(['uint256'], [innerLen])
  return ('0x' +
    r.slice(2) +
    s.slice(2) +
    v +
    lengthHex.slice(2) +
    innerSig.slice(2)) as Hex
}

/**
 * Equivalent to the on-chain SignMessageLib.getMessageHash(_data) — included
 * here mainly so unit tests can cross-check against the pure
 * computeSafeMessageHash() and against on-chain values. Should be identical
 * to computeSafeMessageHash().
 */
export function manualSafeMessageHash(
  chainId: number,
  safeAddress: string,
  innerHash: Hex,
): Hex {
  const SAFE_MSG_TYPEHASH = keccak256(
    toUtf8Bytes('SafeMessage(bytes message)'),
  )
  const DOMAIN_TYPEHASH = keccak256(
    toUtf8Bytes('EIP712Domain(uint256 chainId,address verifyingContract)'),
  )
  const abi = AbiCoder.defaultAbiCoder()
  const domainSeparator = keccak256(
    abi.encode(
      ['bytes32', 'uint256', 'address'],
      [DOMAIN_TYPEHASH, chainId, getAddress(safeAddress)],
    ),
  )
  const safeMessageHashStruct = keccak256(
    abi.encode(
      ['bytes32', 'bytes32'],
      [SAFE_MSG_TYPEHASH, keccak256(innerHash)],
    ),
  )
  return keccak256(
    `0x1901${domainSeparator.slice(2)}${safeMessageHashStruct.slice(2)}` as Hex,
  ) as Hex
}
